import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem, EmployeeAvatar, LocalizedDateInput, Spinner } from '../../../shared/components';
import { formatLongDateLabel, toDateInputValue } from '../../../shared/utils/localization';
import { Employee, EmployeesMetadata } from '../types/Employee';
import { getEmployeeById, getEmployeesMetadata, updateEmployee } from '../services/employeeService';
import {
  buildDepartmentLabelMap,
  buildHierarchicalDepartmentOptions,
  formatDepartmentOptionLabel,
} from '../utils/departmentOptions';
import { listConsultations } from '../../consultations/services/consultationService';
import type { Consultation } from '../../consultations/types/Consultation';
import { RelatedConsultationsPanel } from '../../consultations/components/RelatedConsultationsPanel';
import styles from './EmployeeLayout.module.css';

const DEFAULT_COLOR = '#2563EB';

const resolveDisplaySubtitle = (employee: Employee): string => {
  return employee.positionName || employee.title || employee.contractTypeName || employee.email || '';
};

const resolveEmployeeColor = (colorPreference?: string): string => {
  const fallback = '#94a3b8';
  const value = (colorPreference || '').trim();
  if (!value) return fallback;

  if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
    return CSS.supports('color', value) ? value : fallback;
  }

  return value;
};

const buildPositionOptions = (
  positions: EmployeesMetadata['positions'],
  departmentLabelById: Map<string, string>,
) => {

  return positions
    .map(item => ({
      id: item.id,
      departmentId: item.departmentId,
      label: item.departmentId
        ? `${departmentLabelById.get(item.departmentId) || item.departmentName || ''} / ${item.name}`
        : item.name,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
};

export const EmployeeDetails: React.FC = () => {
  const { t } = useTranslation(['admin', 'consultations', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [employee, setEmployee] = React.useState<Employee | null>(null);
  const [metadata, setMetadata] = React.useState<EmployeesMetadata | null>(null);
  const [consultations, setConsultations] = React.useState<Consultation[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [consultationsLoading, setConsultationsLoading] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<'details' | 'consultations'>('details');
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [errors, setErrors] = React.useState<{ [k: string]: string }>({});
  const [form, setForm] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    title: '',
    dateOfBirth: '',
    employmentDate: '',
    status: '',
    contractTypeId: '',
    positionId: '',
    departmentId: '',
    phoneCountryCode: '',
    phoneNumber: '',
    language: '',
    calendarPreference: '',
    locationId: '',
    colorPreference: DEFAULT_COLOR,
    isActive: true,
    requirePasswordChangeOnFirstLogin: false,
  });
  const departmentOptions = React.useMemo(
    () => buildHierarchicalDepartmentOptions(metadata?.departments || []),
    [metadata?.departments],
  );
  const departmentLabelById = React.useMemo(
    () => buildDepartmentLabelMap(metadata?.departments || []),
    [metadata?.departments],
  );
  const positionOptions = React.useMemo(
    () => buildPositionOptions(metadata?.positions || [], departmentLabelById),
    [departmentLabelById, metadata?.positions],
  );
  const availablePositions = React.useMemo(() => {
    const selectedDepartmentId = (form.departmentId || '').trim();
    return positionOptions.filter(option => !option.departmentId || !selectedDepartmentId || option.departmentId === selectedDepartmentId);
  }, [form.departmentId, positionOptions]);

  React.useEffect(() => {
    if (!form.positionId) {
      return;
    }

    const stillAvailable = availablePositions.some(option => option.id === form.positionId);
    if (!stillAvailable) {
      setForm(prev => ({ ...prev, positionId: '' }));
    }
  }, [availablePositions, form.positionId]);

  React.useEffect(() => {
    const fetchEmployee = async () => {
      setLoading(true);
      try {
        const [metadataValue, data] = await Promise.all([
          getEmployeesMetadata(),
          id?.trim() ? getEmployeeById(id) : Promise.resolve(null),
        ]);

        setMetadata(metadataValue);
        setEmployee(data);
        if (data) {
          setForm({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email,
            title: data.title || '',
            dateOfBirth: toDateInputValue(data.dateOfBirth),
            employmentDate: toDateInputValue(data.employmentDate),
            status: data.status || metadataValue.statuses[0]?.value || '',
            contractTypeId: data.contractTypeId || '',
            positionId: data.positionId || '',
            departmentId: data.departmentId || '',
            phoneCountryCode: data.phoneCountryCode || '',
            phoneNumber: data.phoneNumber || '',
            language: data.language || metadataValue.languages[0]?.code || '',
            calendarPreference: data.calendarPreference || metadataValue.calendarPreferences[0]?.value || '',
            locationId: data.locationId || '',
            colorPreference: data.colorPreference || DEFAULT_COLOR,
            isActive: data.isActive,
            requirePasswordChangeOnFirstLogin: data.requirePasswordChangeOnFirstLogin,
          });
        }
        setGeneralError(null);
      } catch {
        setGeneralError(t('failedToLoadEmployee', { ns: 'admin' }));
      } finally {
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [id, t]);

  React.useEffect(() => {
    if (activeTab !== 'consultations' || !id?.trim()) {
      return;
    }

    let cancelled = false;
    const loadConsultations = async () => {
      setConsultationsLoading(true);
      try {
        const data = await listConsultations({ assignedEmployeeId: id, page: 1, pageSize: 100 });
        if (!cancelled) {
          setConsultations(data);
        }
      } catch {
        if (!cancelled) {
          setConsultations([]);
        }
      } finally {
        if (!cancelled) {
          setConsultationsLoading(false);
        }
      }
    };

    void loadConsultations();
    return () => {
      cancelled = true;
    };
  }, [activeTab, id]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
        <Spinner text={t('loadingEmployee', { ns: 'admin' })} />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className={styles.page}>
        <h2>{t('employeeNotFound', { ns: 'admin' })}</h2>
        <button className="btn btn-secondary" onClick={() => navigate('/members')}>{t('backToTeamMembers', { ns: 'admin' })}</button>
      </div>
    );
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: t('admin', { ns: 'admin' }), path: '/admin' },
    { label: t('teamMembers', { ns: 'admin' }), path: '/members' },
    { label: employee.name || t('employeeDetails', { ns: 'admin' }), path: `/members/${employee.id}` },
  ];
  const accent = resolveEmployeeColor(form.colorPreference);

  const isDirty =
    form.firstName !== (employee.firstName || '') ||
    form.lastName !== (employee.lastName || '') ||
    form.email !== employee.email ||
    form.title !== (employee.title || '') ||
    form.dateOfBirth !== toDateInputValue(employee.dateOfBirth) ||
    form.employmentDate !== toDateInputValue(employee.employmentDate) ||
    form.status !== (employee.status || '') ||
    form.contractTypeId !== (employee.contractTypeId || '') ||
    form.positionId !== (employee.positionId || '') ||
    form.departmentId !== (employee.departmentId || '') ||
    form.phoneCountryCode !== (employee.phoneCountryCode || '') ||
    form.phoneNumber !== (employee.phoneNumber || '') ||
    form.language !== (employee.language || '') ||
    form.calendarPreference !== (employee.calendarPreference || '') ||
    form.locationId !== (employee.locationId || '') ||
    form.colorPreference !== (employee.colorPreference || '') ||
    form.isActive !== employee.isActive ||
    form.requirePasswordChangeOnFirstLogin !== employee.requirePasswordChangeOnFirstLogin;

  const validate = () => {
    const nextErrors: { [k: string]: string } = {};

    if (!form.firstName.trim() && !form.lastName.trim()) {
      nextErrors.firstName = t('nameRequired', { ns: 'admin' });
    }

    const email = form.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors.email = t('invalidEmailAddress', { ns: 'admin' });
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onEdit = () => {
    setGeneralError(null);
    setErrors({});
    setEditMode(true);
  };

  const onCancel = () => {
    setForm({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      email: employee.email,
      title: employee.title || '',
      dateOfBirth: toDateInputValue(employee.dateOfBirth),
      employmentDate: toDateInputValue(employee.employmentDate),
      status: employee.status || metadata?.statuses[0]?.value || '',
      contractTypeId: employee.contractTypeId || '',
      positionId: employee.positionId || '',
      departmentId: employee.departmentId || '',
      phoneCountryCode: employee.phoneCountryCode || '',
      phoneNumber: employee.phoneNumber || '',
      language: employee.language || metadata?.languages[0]?.code || '',
      calendarPreference: employee.calendarPreference || metadata?.calendarPreferences[0]?.value || '',
      locationId: employee.locationId || '',
      colorPreference: employee.colorPreference || DEFAULT_COLOR,
      isActive: employee.isActive,
      requirePasswordChangeOnFirstLogin: employee.requirePasswordChangeOnFirstLogin,
    });
    setGeneralError(null);
    setErrors({});
    setEditMode(false);
  };

  const onSave = async () => {
    if (!isDirty) return;
    if (!validate()) return;

    setSaving(true);
    setGeneralError(null);
    try {
      const updated = await updateEmployee(employee.id, {
        firstName: form.firstName.trim() || undefined,
        lastName: form.lastName.trim() || undefined,
        email: form.email.trim() || undefined,
        title: form.title.trim() || undefined,
        dateOfBirth: form.dateOfBirth || undefined,
        employmentDate: form.employmentDate || undefined,
        status: form.status || undefined,
        contractTypeId: form.contractTypeId || undefined,
        positionId: form.positionId || undefined,
        clearPosition: !form.positionId && Boolean(employee.positionId) ? true : undefined,
        departmentId: form.departmentId || undefined,
        clearDepartment: !form.departmentId && Boolean(employee.departmentId) ? true : undefined,
        phoneCountryCode: form.phoneCountryCode.trim() || undefined,
        phoneNumber: form.phoneNumber.trim() || undefined,
        language: form.language || undefined,
        calendarPreference: form.calendarPreference || undefined,
        locationId: form.locationId || undefined,
        colorPreference: form.colorPreference.trim() || undefined,
        isActive: form.isActive,
        requirePasswordChangeOnFirstLogin: form.requirePasswordChangeOnFirstLogin,
      });

      if (!updated) {
        setGeneralError(t('failedToSaveEmployeeNotFound', { ns: 'admin' }));
        return;
      }

      setEmployee(updated);
      setEditMode(false);
    } catch {
      setGeneralError(t('failedToSaveEmployee', { ns: 'admin' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <Breadcrumb items={breadcrumbItems} />
      <div className={styles.header}>
        <div className={styles.identityHero}>
          <EmployeeAvatar
            name={employee.name}
            imageUrl={employee.profileImageUrl}
            colorPreference={employee.colorPreference}
            className={styles.identityAvatar}
            size={72}
          />
          <div className={styles.identityCopy}>
            <h2 className={styles.title}>{t('employeeDetails', { ns: 'admin' })}</h2>
            <p className={styles.identitySubtitle}>{resolveDisplaySubtitle(employee) || t('teamMembers', { ns: 'admin' })}</p>
          </div>
        </div>
      </div>

      {generalError && <div className={`error ${styles.errorBanner}`}>{generalError}</div>}

      {!editMode ? (
        <>
          <div className={styles.tabs}>
            <button
              type="button"
              className={activeTab === 'details' ? styles.tabButtonActive : styles.tabButton}
              onClick={() => setActiveTab('details')}
            >
              {t('employeeDetails', { ns: 'admin' })}
            </button>
            <button
              type="button"
              className={activeTab === 'consultations' ? styles.tabButtonActive : styles.tabButton}
              onClick={() => setActiveTab('consultations')}
            >
              {t('consultations', { ns: 'consultations', defaultValue: 'Consultations' })}
            </button>
          </div>

          {activeTab === 'details' ? (
            <>
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('firstName', { ns: 'admin' })}</p>
              <p className={`${styles.detailValue} ${employee.firstName ? '' : styles.mutedValue}`}>{employee.firstName || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('lastName', { ns: 'admin' })}</p>
              <p className={`${styles.detailValue} ${employee.lastName ? '' : styles.mutedValue}`}>{employee.lastName || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('email', { ns: 'admin' })}</p>
              <p className={`${styles.detailValue} ${employee.email ? '' : styles.mutedValue}`}>
                {employee.email ? <a className={styles.detailLink} href={`mailto:${employee.email}`}>{employee.email}</a> : '-'}
              </p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('title', { ns: 'admin' })}</p>
              <p className={`${styles.detailValue} ${employee.title ? '' : styles.mutedValue}`}>{employee.title || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('position', { ns: 'admin' })}</p>
              <p className={`${styles.detailValue} ${employee.positionName ? '' : styles.mutedValue}`}>{employee.positionName || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('contractType', { ns: 'admin' })}</p>
              <p className={`${styles.detailValue} ${employee.contractTypeName ? '' : styles.mutedValue}`}>{employee.contractTypeName || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('department', { ns: 'admin' })}</p>
              <p className={`${styles.detailValue} ${employee.departmentName ? '' : styles.mutedValue}`}>{employee.departmentName || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('location', { ns: 'admin' })}</p>
              <p className={`${styles.detailValue} ${employee.locationName ? '' : styles.mutedValue}`}>{employee.locationName || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('language', { ns: 'admin' })}</p>
              <p className={`${styles.detailValue} ${employee.language ? '' : styles.mutedValue}`}>{employee.language || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('calendarPreference', { ns: 'admin' })}</p>
              <p className={`${styles.detailValue} ${employee.calendarPreference ? '' : styles.mutedValue}`}>{employee.calendarPreference || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('employmentDate', { ns: 'admin' })}</p>
              <p className={`${styles.detailValue} ${employee.employmentDate ? '' : styles.mutedValue}`}>{toDateInputValue(employee.employmentDate) || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('dateOfBirth', { ns: 'admin' })}</p>
              <p className={`${styles.detailValue} ${employee.dateOfBirth ? '' : styles.mutedValue}`}>
                {employee.dateOfBirth ? formatLongDateLabel(toDateInputValue(employee.dateOfBirth)) : '-'}
              </p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('phoneNumber', { ns: 'admin' })}</p>
              <p className={`${styles.detailValue} ${employee.phoneNumber ? '' : styles.mutedValue}`}>{[employee.phoneCountryCode, employee.phoneNumber].filter(Boolean).join(' ') || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('status', { ns: 'admin' })}</p>
              <span className={`${styles.statusBadge} ${employee.isActive ? styles.statusActive : styles.statusInactive}`}>
                {employee.isActive ? t('active', { ns: 'admin' }) : t('inactive', { ns: 'admin' })}
              </span>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('colorPreference', { ns: 'admin' })}</p>
              <span className={styles.colorSwatchRow}>
                <span
                  aria-hidden
                  className={styles.colorDot}
                  style={{
                    backgroundColor: resolveEmployeeColor(employee.colorPreference),
                    boxShadow: `0 0 0 2px ${resolveEmployeeColor(employee.colorPreference)}22`,
                  }}
                />
                <span className={`${styles.detailValue} ${employee.colorPreference ? '' : styles.mutedValue}`}>
                  {employee.colorPreference || t('defaultColor', { ns: 'admin' })}
                </span>
              </span>
            </div>
            <div className={`${styles.detailItem} ${styles.detailItemWide}`}>
              <p className={styles.detailLabel}>{t('requirePasswordChangeOnFirstLogin', { ns: 'admin' })}</p>
              <p className={styles.detailValue}>{employee.requirePasswordChangeOnFirstLogin ? t('yes', { ns: 'admin' }) : t('no', { ns: 'admin' })}</p>
            </div>
          </div>

          <div className={styles.actions}>
            <button className="btn btn-primary" onClick={onEdit}>{t('edit', { ns: 'common' })}</button>
            <button className="btn btn-secondary" onClick={() => navigate('/members')}>{t('backToTeamMembers', { ns: 'admin' })}</button>
          </div>
            </>
          ) : consultationsLoading ? (
            <Spinner text={t('loadingConsultations', { ns: 'admin', defaultValue: 'Loading consultations…' })} />
          ) : (
            <RelatedConsultationsPanel
              consultations={consultations}
              emptyLabel={t('noEmployeeConsultations', { ns: 'admin', defaultValue: 'No consultations are assigned to this employee.' })}
              returnTo={`/members/${employee.id}`}
            />
          )}
        </>
      ) : (
        <>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('firstName', { ns: 'admin' })}
              <input className={styles.input} value={form.firstName} onChange={e => setForm(prev => ({ ...prev, firstName: e.target.value }))} />
            </label>
            {errors.firstName && <div className={styles.fieldError}>{errors.firstName}</div>}
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('lastName', { ns: 'admin' })}
              <input className={styles.input} value={form.lastName} onChange={e => setForm(prev => ({ ...prev, lastName: e.target.value }))} />
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('email', { ns: 'admin' })}
              <input className={styles.input} value={form.email} onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))} />
            </label>
            {errors.email && <div className={styles.fieldError}>{errors.email}</div>}
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('title', { ns: 'admin' })}
              <input className={styles.input} value={form.title} onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))} />
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('position', { ns: 'admin' })}
              <select className={`${styles.input} ${styles.select}`} value={form.positionId} onChange={e => setForm(prev => ({ ...prev, positionId: e.target.value }))}>
                <option value="">{t('selectPosition', { ns: 'admin' })}</option>
                {availablePositions.map(option => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('contractType', { ns: 'admin' })}
              <select className={`${styles.input} ${styles.select}`} value={form.contractTypeId} onChange={e => setForm(prev => ({ ...prev, contractTypeId: e.target.value }))}>
                <option value="">{t('selectContractType', { ns: 'admin' })}</option>
                {(metadata?.contractTypes || []).map(option => (
                  <option key={option.id} value={option.id}>{option.name}</option>
                ))}
              </select>
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('department', { ns: 'admin' })}
              <select className={`${styles.input} ${styles.select}`} value={form.departmentId} onChange={e => setForm(prev => ({ ...prev, departmentId: e.target.value }))}>
                <option value="">{t('selectDepartment', { ns: 'admin' })}</option>
                {departmentOptions.map(option => (
                  <option
                    key={option.id}
                    value={option.id}
                    className={option.hasChildren ? styles.departmentParentOption : undefined}
                    style={option.hasChildren ? { fontWeight: 700 } : undefined}
                  >
                    {formatDepartmentOptionLabel(option.name, option.depth)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('location', { ns: 'admin' })}
              <select className={`${styles.input} ${styles.select}`} value={form.locationId} onChange={e => setForm(prev => ({ ...prev, locationId: e.target.value }))}>
                <option value="">{t('selectLocation', { ns: 'admin' })}</option>
                {(metadata?.locations || []).map(option => (
                  <option key={option.id} value={option.id}>{option.name}</option>
                ))}
              </select>
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('language', { ns: 'admin' })}
              <select className={`${styles.input} ${styles.select}`} value={form.language} onChange={e => setForm(prev => ({ ...prev, language: e.target.value }))}>
                <option value="">{t('selectLanguage', { ns: 'admin' })}</option>
                {(metadata?.languages || []).map(option => (
                  <option key={option.code} value={option.code}>{option.name}</option>
                ))}
              </select>
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('calendarPreference', { ns: 'admin' })}
              <select className={`${styles.input} ${styles.select}`} value={form.calendarPreference} onChange={e => setForm(prev => ({ ...prev, calendarPreference: e.target.value }))}>
                <option value="">{t('selectCalendarPreference', { ns: 'admin' })}</option>
                {(metadata?.calendarPreferences || []).map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('employmentDate', { ns: 'admin' })}
              <input className={styles.input} type="date" value={form.employmentDate} onChange={e => setForm(prev => ({ ...prev, employmentDate: e.target.value }))} />
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('dateOfBirth', { ns: 'admin' })}
              <LocalizedDateInput
                className={styles.input}
                displayFormat="long"
                value={form.dateOfBirth}
                onValueChange={dateOfBirth => setForm(prev => ({ ...prev, dateOfBirth }))}
                fillWidth
              />
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('phoneCountryCode', { ns: 'admin' })}
              <input className={styles.input} value={form.phoneCountryCode} onChange={e => setForm(prev => ({ ...prev, phoneCountryCode: e.target.value }))} />
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('phoneNumber', { ns: 'admin' })}
              <input className={styles.input} value={form.phoneNumber} onChange={e => setForm(prev => ({ ...prev, phoneNumber: e.target.value }))} />
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>{t('colorPreference', { ns: 'admin' })}</label>
            <div className={styles.colorField}>
              <input className={styles.colorInput} type="color" value={form.colorPreference || DEFAULT_COLOR} onChange={e => setForm(prev => ({ ...prev, colorPreference: e.target.value }))} />
              <span className={styles.colorValue}>{form.colorPreference || DEFAULT_COLOR}</span>
              <span
                aria-hidden
                className={styles.colorDot}
                style={{ backgroundColor: accent, boxShadow: `0 0 0 2px ${accent}22` }}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('employmentStatus', { ns: 'admin' })}
              <select className={`${styles.input} ${styles.select}`} value={form.status} onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}>
                <option value="">{t('selectEmploymentStatus', { ns: 'admin' })}</option>
                {(metadata?.statuses || []).map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              {t('status', { ns: 'admin' })}
              <select className={`${styles.input} ${styles.select}`} value={form.isActive ? 'active' : 'inactive'} onChange={e => setForm(prev => ({ ...prev, isActive: e.target.value === 'active' }))}>
                <option value="active">{t('active', { ns: 'admin' })}</option>
                <option value="inactive">{t('inactive', { ns: 'admin' })}</option>
              </select>
            </label>
          </div>

          <div className={`${styles.field} ${styles.fieldFull}`}>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.requirePasswordChangeOnFirstLogin}
                onChange={e => setForm(prev => ({ ...prev, requirePasswordChangeOnFirstLogin: e.target.checked }))}
              />
              {t('requirePasswordChangeOnFirstLogin', { ns: 'admin' })}
            </label>
          </div>
        </div>

          <div className={styles.actions}>
            <button className="btn btn-primary" onClick={onSave} disabled={saving || !isDirty}>{saving ? t('saving', { ns: 'common' }) : t('save', { ns: 'admin' })}</button>
            <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>{t('cancel', { ns: 'common' })}</button>
          </div>
        </>
      )}
    </div>
  );
};

export default EmployeeDetails;